// Extracted from chat/UXView.tsx so it can also be rendered inside AssessmentCaptureCard
// (WorkflowCard imports it too — UXView.tsx already imports WorkflowCard, so UXView could not be
// the source without a circular import). Behavior is unchanged from the original inline version.
import { useState } from 'react';
import { useCreatePropertyMeasurementMutation } from '@/redux/internal-services/workspaceApiExtensions';
import { CardSection } from '@/components/UI/CardSection';
import { humanizeLabel, fmtDate } from '@/components/chat/ChatPills';

export interface PropertyMeasurementLite {
  measurement_id: string;
  how_obtained: string;
  provider?: string | null;
  measured_on?: string | null;
  is_current_reference: boolean;
  values: { squares?: number | null; pitch?: string | null; ridge_length_ft?: number | null; total_area_sqft?: number | null };
  report_url?: string | null;
  report_filename?: string | null;
  notes?: string | null;
}

export function PropertyMeasurementsSection({ propertyId, engagementId, currentMeasurement, measurements, readOnly }: {
  propertyId: string;
  engagementId?: string;
  currentMeasurement?: { squares?: number | null; pitch?: string | null; provider?: string | null; how_obtained: string; notes?: string | null } | null;
  measurements: PropertyMeasurementLite[];
  readOnly?: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createM, { isLoading }] = useCreatePropertyMeasurementMutation();

  async function submit() {
    setError(null);
    const trimmed = text.trim();
    if (!trimmed) { setError('Enter the measurement details.'); return; }
    try {
      await createM({
        propertyId,
        body: {
          source_engagement_id: engagementId,
          how_obtained: 'manual',
          notes: trimmed,
          set_as_current_reference: true,
        },
      }).unwrap();
      setText('');
      setFormOpen(false);
    } catch {
      setError('Could not save the measurement — try again.');
    }
  }

  return (
    <>
      {currentMeasurement && !formOpen && (
        <div className="rounded-xl border border-ui-border bg-ui-card px-3 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-sm font-semibold text-ui-text">Measurements</h3>
            <div className="flex items-center gap-2">
              <span className="text-micro font-medium text-ui-text3 uppercase tracking-wide">{measurements.length} set{measurements.length === 1 ? '' : 's'} on file</span>
              {!readOnly && (
                <button type="button" onClick={() => setFormOpen(true)} className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent/10 text-accent-text hover:bg-accent/15 transition-colors">+ Add</button>
              )}
            </div>
          </div>
          <div className="text-xs text-ui-text2 flex items-center gap-2 flex-wrap">
            {(currentMeasurement.squares != null || currentMeasurement.pitch) ? (
              <>
                <span className="font-semibold text-ui-text">{currentMeasurement.squares ?? '—'} squares</span>
                <span>·</span>
                <span>pitch {currentMeasurement.pitch || '—'}</span>
              </>
            ) : currentMeasurement.notes ? (
              <span className="text-ui-text truncate">{currentMeasurement.notes}</span>
            ) : (
              <span className="text-ui-text3">No details recorded</span>
            )}
            {currentMeasurement.provider && (<><span>·</span><span className="text-ui-text3">{currentMeasurement.provider} ({humanizeLabel(currentMeasurement.how_obtained)})</span></>)}
          </div>
          {/* All sets on file — each row exposes its source report (EagleView PDF etc.) as a
              download link, so a contractor can pull the original artifact for the next job.
              The `is_current_reference` set is called out with a tag; older sets stay accessible
              per FR-03-017 (non-destructive supersession). */}
          {measurements.length > 0 && (
            <div className="mt-2.5 pt-2 border-t border-ui-border flex flex-col gap-1.5">
              {measurements.map(m => (
                <div key={m.measurement_id} className="flex items-center gap-2 text-xs">
                  {m.is_current_reference && (
                    <span className="flex-shrink-0 text-micro font-bold px-1.5 py-0.5 rounded uppercase tracking-wide bg-accent/10 text-accent-text">Current</span>
                  )}
                  <span className="text-ui-text2 truncate flex-1">
                    {(m.values.squares != null || m.values.pitch) ? (
                      <>
                        <span className="font-medium text-ui-text">{m.values.squares ?? '—'} sq</span>
                        {m.values.pitch && <> · {m.values.pitch}</>}
                      </>
                    ) : m.notes ? (
                      <span className="font-medium text-ui-text">{m.notes}</span>
                    ) : null}
                    {m.provider && <> · {m.provider}</>}
                    <span className="text-ui-text3"> ({humanizeLabel(m.how_obtained)})</span>
                    {m.measured_on && <span className="text-ui-text3"> · {fmtDate(m.measured_on)}</span>}
                  </span>
                  {m.report_url ? (
                    <a
                      href={m.report_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 flex items-center gap-1 text-accent-text hover:opacity-80 font-medium"
                      title={m.report_filename || 'Open report'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      Report
                    </a>
                  ) : (
                    <span className="flex-shrink-0 text-ui-text3">—</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {(!currentMeasurement || formOpen) && (
        <CardSection
          title="Measurements"
          onAdd={!readOnly && !formOpen ? () => setFormOpen(true) : undefined}
          addLabel="Add Measurement"
          emptyText={!formOpen && !currentMeasurement
            ? readOnly ? "No measurements yet. Add them from inside an engagement."
                       : "No measurements yet. Describe them in your own words — squares, pitch, dimensions — or paste them from a report."
            : undefined}
        >
          {formOpen && (
            <div className="rounded-lg border border-ui-border bg-ui-card p-3 flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-micro text-ui-text3 uppercase tracking-wide">Measurement details</span>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  rows={4}
                  placeholder="e.g. 24.5 squares, 6/12 pitch, 42 ft ridge — or paste from a report"
                  className="rounded-lg border border-ui-border bg-ui-subtle px-2 py-1.5 text-sm focus:outline-none focus:border-ui-ring resize-y"
                />
              </label>
              {error && <p className="text-xs text-danger-text">{error}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setFormOpen(false)} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
                <button type="button" disabled={isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full btn-sheen text-on-primary disabled:opacity-40">
                  {isLoading ? 'Saving…' : 'Save measurement'}
                </button>
              </div>
            </div>
          )}
        </CardSection>
      )}
    </>
  );
}

export default PropertyMeasurementsSection;
